// react components
import React, {
    useCallback,
    useEffect,
    useState,
} from 'react'
import {
    IonCheckbox,
    IonItem,
    IonLabel,
    IonList,
} from '@ionic/react'
import axios from 'axios'
import debounce from 'lodash.debounce'
import {
    useDispatch,
    useSelector,
} from 'react-redux'
import AsyncSelect from 'react-select/async'

// data
import {
    defaultReduxState,
    reduxModalErrorEventHandlerSite,
} from 'data'

// pages
import {
    ErrorHelper825,
    FormLabel825,
} from 'pages'

// serializers
import {
    CityTempSerializer,
    TranslationHelper,
} from 'serializers/site'

// services
import {
    axiosErrorHandlerSite,
    getApiUrlSiteV2,
    getAxiosHeadersSite,
} from 'services'

// props
type LocationInputV2825Props = {
    canGetProfileAddress?: boolean
    clearable?: boolean
    disabled?: boolean
    error?: string
    helperText?: string
    label?: string
    marginBottom?: number
    mother?: any
    multi?: boolean
    name: string
    onChange: React.Dispatch<any>
    onRefresh?: () => void
    placeholder?: string
    translation?: TranslationHelper
    value: any
}

// main
export const LocationInputV2825: React.FC<LocationInputV2825Props> = React.memo(({
    canGetProfileAddress,
    clearable = true,
    disabled,
    error,
    helperText,
    label,
    marginBottom,
    mother,
    multi,
    name,
    onChange,
    onRefresh,
    placeholder,
    translation,
    value,
}) => {

    const dispatch = useDispatch()
    const reduxAuth = useSelector((state: defaultReduxState) => state.reduxAuth)
    const reduxModalSite = useSelector((state: defaultReduxState) => state.reduxModalSite)
    const reduxText = useSelector((state: defaultReduxState) => state.reduxText.data)

    useEffect(() => {
        setValue(value || (multi ? [] : ''))
    }, [value])

    const [newValue, setValue] = useState<CityTempSerializer[]>(value || (multi ? [] : ''))

    function onInputChange(value: any) {
        try {
            if (value === newValue) return
            setValue(value)
            onChange({
                name: name,
                value: value,
            })
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'LocationInput',
                'onInputChange',
            ))
        }
    }

    async function getOptions(inputValue: string) {
        const getUrl = getApiUrlSiteV2(`main/helper/googleplace/autocomplete/?search=${inputValue}`, reduxModalSite)
        if (process.env.NODE_ENV === 'development') console.log(getUrl)
        try {
            const response = await fetch(getUrl, reduxAuth?.axiosConfig)
            const json = await response.json()
            return json
        } catch (error) {
            axiosErrorHandlerSite({
                apiUrl: getUrl,
                component: 'LocationInput',
                dispatch,
                error,
                reduxAuth,
                reduxModalSite,
                reference: 'getOptions',
            })
        }
    }

    const debouncedGetOptions = useCallback(
        debounce((inputValue: string, callback: (options: any) => void) => {
            getOptions(inputValue).then(callback)
        }, 500),
        []
    )

    const promiseOptions = (inputValue: string) =>
        new Promise(resolve => {
            debouncedGetOptions(inputValue, resolve)
        })

    function getPlaceDetail(e: any) {
        try {
            if (multi) {
                if (e?.length === 0) {
                    onInputChange(e)
                    return
                }
                const placedToCheck: CityTempSerializer | undefined = e.find(
                    (place: CityTempSerializer) => !newValue.some((item: any) => item.placeId === place.placeId)
                )
                if (!placedToCheck) {
                    onInputChange(e)
                } else {
                    const refreshAxiosHeaders = getAxiosHeadersSite(reduxAuth, reduxModalSite, dispatch)
                    const getUrl = getApiUrlSiteV2(`main/helper/googleplace/detail/?place_id=${placedToCheck.placeId}&description=${placedToCheck.description}`, reduxModalSite)
                    if (process.env.NODE_ENV === 'development') console.log(getUrl)
                    axios({
                        headers: refreshAxiosHeaders,
                        method: 'get',
                        url: getUrl,
                    })
                        .then((response) => {
                            onInputChange([...newValue, ...response.data])
                        })
                        .catch((error) => {
                            axiosErrorHandlerSite({
                                apiUrl: getUrl,
                                component: 'LocationInput',
                                dispatch,
                                error,
                                reduxAuth,
                                reduxModalSite,
                                reference: 'getPlaceDetail',
                            })
                        })
                }
            } else {
                if (!e?.placeId) {
                    onInputChange(e)
                } else {
                    const refreshAxiosHeaders = getAxiosHeadersSite(reduxAuth, reduxModalSite, dispatch)
                    const getUrl = getApiUrlSiteV2(`main/helper/googleplace/detail/?place_id=${e.placeId}&description=${e.description}`, reduxModalSite)
                    if (process.env.NODE_ENV === 'development') console.log(getUrl)
                    axios({
                        headers: refreshAxiosHeaders,
                        method: 'get',
                        url: getUrl,
                    })
                        .then((response) => {
                            onInputChange(response.data)
                        })
                        .catch((error) => {
                            axiosErrorHandlerSite({
                                apiUrl: getUrl,
                                component: 'LocationInput',
                                dispatch,
                                error,
                                reduxAuth,
                                reduxModalSite,
                                reference: 'getPlaceDetail',
                            })
                        })
                }
            }
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'LocationInput',
                'getPlaceDetail',
            ))
        }
    }

    function getProfileAddress() {
        try {
            const valueObject = {
                name: 'address_json',
                value: mother.fields.profile.address_json,
            }
            setValue(mother.fields.profile.address_json)
            onChange(valueObject)
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'LocationInput',
                'getProfileAddress',
            ))
        }
    }

    const customStyles = {
        clearIndicator: (provided: any, state: any) => ({
            ...provided,
            cursor: 'pointer',
        }),
        control: (provided: any, state: any) => ({
            ...provided,
            background: '#f2f2f2',
            border: '1px solid #e4e4e4',
            borderRadius: '8px',
            ':hover': {
                ...provided[':hover'],
                border: '1px solid #e4e4e4',
                boxShadow: 'none',
            },
            ':focus': {
                ...provided[':focus'],
                boxShadow: '0 0 0 2px #ffffff',
                outline: '1px solid #ffffff',
            },
        }),
        input: (provided: any, state: any) => ({
            ...provided,
            color: '#000000',
        }),
        menu: (provided: any, state: any) => ({
            ...provided,
            background: '#f2f2f2',
            borderRadius: '8px',
            boxShadow: '2px 6px 21px -2px rgba(0,0,0,0.75)',
            zIndex: 9999,
        }),
        menuList: (provided: any, state: any) => ({
            ...provided,
            boxShadow: '0 0 4px 2px rgba(var(--mo-color-mo-color-rgb), 0.70)',
            border: '1px solid var(--mo-color-mo-color)',
            maxHeight: '20vh',
            zIndex: 9999,
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            background: '#f2f2f2',
            borderBottom: '1px solid #e4e4e4',
            color: state.isSelected ? 'var(--mo-color-mo-color)' : '#000000',
            ':active': {
                ...provided[':active'],
                backgroundColor: '#f2f2f2',
                color: 'var(--mo-color-mo-color)',
            },
            ':hover': {
                ...provided[':hover'],
                backgroundColor: 'var(--mo-color-mo-color)',
                color: '#000000',
                cursor: 'pointer',
            },
        }),
        singleValue: (provided: any, state: any) => ({
            ...provided,
            color: '#000000',
        }),
    }

    if (label) {
        return (
            <div
                className={`input-wrap-825${disabled ? ' disabled' : ''}`}
                style={{
                    marginBottom: marginBottom ? `${marginBottom}px` : undefined,
                }}
            >
                <div className='input-label-wrap'>
                    <FormLabel825
                        formInput='address_json'
                        helperText={helperText}
                        label={label || ''}
                        name={name}
                        onRefresh={onRefresh}
                        translation={translation}
                        translationDefaultValue={value}
                    />
                </div>
                <div className='location-input-825'>
                    <AsyncSelect
                        cacheOptions
                        getOptionLabel={option => (option.description_clean || option.description)}
                        getOptionValue={(option: any) => option.placeId}
                        isClearable={clearable}
                        isDisabled={disabled}
                        isMulti={multi}
                        //@ts-ignore
                        loadOptions={promiseOptions}
                        menuPlacement='auto'
                        noOptionsMessage={(e: { inputValue: string }) => e.inputValue ? reduxText[9754] : reduxText[9755]}
                        onChange={getPlaceDetail}
                        placeholder={placeholder || reduxText[9755]}
                        styles={customStyles}
                        value={newValue}
                    />
                </div>
                <ErrorHelper825 error={error} />
            </div>
        )
    }

    return (
        <div className='location-input-825'>
            <AsyncSelect
                cacheOptions
                getOptionLabel={option => (option.description_clean || option.description)}
                getOptionValue={(option: any) => option.placeId}
                isClearable={clearable}
                isDisabled={disabled}
                isMulti={multi}
                //@ts-ignore
                loadOptions={promiseOptions}
                menuPlacement='auto'
                noOptionsMessage={(e: { inputValue: string }) => e.inputValue ? reduxText[9754] : reduxText[9755]}
                onChange={getPlaceDetail}
                placeholder={placeholder || reduxText[9755]}
                styles={customStyles}
                value={newValue}
            />
        </div>
    )
})
